import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ToggleSwitch from '../../../../components/ToggleSwitch/ToggleSwitch';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react'; // Importando o ícone Info
import { COMMON_COUNTRIES } from '../../../../constants/countries';
import toast from 'react-hot-toast';

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (leadData: any) => void;
}

const AddLeadModal = ({ isOpen, onClose, onSave }: AddLeadModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    category: '',
    country: '',
    rating: 0,
    hasWhatsapp: true,
    prospected: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleToggleWhatsapp = () => {
    setFormData(prev => ({
      ...prev,
      hasWhatsapp: !prev.hasWhatsapp,
    }));
  };

  const handleToggleProspected = () => {
    setFormData(prev => ({
      ...prev,
      prospected: !prev.prospected,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validação: Se marcou "Possui WhatsApp", deve ter país selecionado
    if (formData.hasWhatsapp && !formData.country) {
      toast.error(t('Por favor, selecione o país para verificar o WhatsApp.'));
      return;
    }

    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 to-orange-400/10 via-transparent backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-2xl rounded-xl bg-white dark:bg-background-dark shadow-2xl border border-gray-200 dark:border-gray-700/50"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700/50 rounded-t-xl">
          <div className="flex flex-col">
            <h3 className="text-xl font-bold text-[#181411] dark:text-white">{t('modals.save_lead_title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('modals.save_lead_subtitle')}</p>
          </div>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </motion.button>
        </div>

        {/* Body / Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              {/* Nome da Empresa (Obrigatório) */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="companyName">{t('lead_form.company_name')}</label>
                <input
                  className="form-input w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 focus:ring-primary focus:border-primary text-[#181411] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 py-2.5 px-4"
                  id="companyName"
                  placeholder={t('lead_form.company_placeholder')}
                  type="text"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Telefone (Obrigatório) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="phone">
                  <div className="flex items-center gap-1.5">
                    {t('lead_form.phone')}
                    <span title={t('lead_form.phone_tooltip')} className="cursor-help">
                      <Info className="size-4 text-gray-400" />
                    </span>
                  </div>
                </label>
                <input
                  className="form-input w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 focus:ring-primary focus:border-primary text-[#181411] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 py-2.5 px-4"
                  id="phone"
                  placeholder={t('lead_form.phone_placeholder_e164')}
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* E-mail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="email">{t('lead_form.email')}</label>
                <input
                  className="form-input w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 focus:ring-primary focus:border-primary text-[#181411] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 py-2.5 px-4"
                  id="email"
                  placeholder={t('lead_form.email_placeholder')}
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              {/* Website */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="website">{t('lead_form.website')}</label>
                <input
                  className="form-input w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 focus:ring-primary focus:border-primary text-[#181411] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 py-2.5 px-4"
                  id="website"
                  placeholder={t('lead_form.website_placeholder')}
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>

              {/* Endereço */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="address">{t('lead_form.address')}</label>
                <input
                  className="form-input w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 focus:ring-primary focus:border-primary text-[#181411] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 py-2.5 px-4"
                  id="address"
                  placeholder={t('lead_form.address_placeholder')}
                  type="text"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="category">{t('lead_form.category')}</label>
                <input
                  className="form-input w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 focus:ring-primary focus:border-primary text-[#181411] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 py-2.5 px-4"
                  id="category"
                  placeholder={t('lead_form.category_placeholder')}
                  type="text"
                  value={formData.category}
                  onChange={handleChange}
                />
              </div>

              {/* País (Country) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="country">{t('lead_form.country')}</label>
                <select
                  className="form-select w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 focus:ring-primary focus:border-primary text-[#181411] dark:text-white py-2.5 px-4"
                  id="country"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option value="">{t('lead_form.country_placeholder')}</option>
                  {COMMON_COUNTRIES.map(country => (
                    <option key={country.code} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rating (Estrelas) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('lead_form.rating')}</label>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <span className={`material-symbols-outlined text-2xl ${star <= formData.rating
                        ? 'text-yellow-400'
                        : 'text-gray-300 dark:text-gray-600'
                        }`}>
                        {star <= formData.rating ? 'star' : 'star_outline'}
                      </span>
                    </button>
                  ))}
                  {formData.rating > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating: 0 }))}
                      className="ml-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {t('lead_form.clear_rating')}
                    </button>
                  )}
                </div>
              </div>

              {/* Possui WhatsApp Toggle */}
              <div className="flex items-center justify-end md:justify-start pt-6 md:pt-0">
                <label className="flex items-center cursor-pointer space-x-3" htmlFor="whatsapp-toggle">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('lead_form.has_whatsapp')}</span>
                  <ToggleSwitch enabled={formData.hasWhatsapp} onToggle={handleToggleWhatsapp} />
                </label>
              </div>

              {/* Já Prospectado Toggle */}
              <div className="flex items-center justify-end md:justify-start pt-6 md:pt-0">
                <label className="flex items-center cursor-pointer space-x-3" htmlFor="prospected-toggle">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('Já Prospectado')}</span>
                  <ToggleSwitch enabled={formData.prospected} onToggle={handleToggleProspected} />
                </label>
              </div>
            </div>
          </div>

          {/* Footer / Actions */}
          <div className="flex items-center justify-end p-6 space-x-2 border-t border-gray-200 dark:border-gray-700/50 rounded-b-xl">
            <motion.button
              type="button"
              onClick={onClose}
              className="flex h-12 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700/50 text-[#181411] dark:text-gray-300 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('modals.cancel')}
            </motion.button>
            <motion.button
              type="submit"
              className="flex h-12 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:opacity-90 transition-opacity"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="material-symbols-outlined">save</span>
              {t('modals.save_lead_button')}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AddLeadModal;